import React, {useState, useEffect, useMemo} from 'react';
import {useNavigate} from 'react-router';
import {
    Box,
    Typography,
    Container,
    Card,
    CardContent,
    Grid,
    Paper,
    CircularProgress,
    Button,
    CardActionArea
} from '@mui/material';
import {
    AdminPanelSettings as AdminIcon,
    People as UsersIcon,
    School as CourseIcon,
    Assessment as AnalyticsIcon,
    ArrowForward as ArrowIcon
} from '@mui/icons-material';
import {createApiClient} from '../../services/ApiClient';
import UserAnalyticsChart from '../../components/UserAnalyticsChart';

const AdminDashboard = ({accessToken}) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        totalUsers: 0,
        totalCourses: 0
    });
    const [error, setError] = useState(null);

    // Create API client
    const apiClient = useMemo(() => {
        return accessToken ? createApiClient(accessToken) : null;
    }, [accessToken]);

    useEffect(() => {
        if (apiClient) {
            fetchAdminStats();
        }
    }, [apiClient]);

    const fetchAdminStats = async () => {
        try {
            setLoading(true);
            setError(null);

            // Fetch users and courses in parallel
            const [usersResponse, coursesResponse] = await Promise.all([
                apiClient.get('/api/users/'),
                apiClient.get('/api/courses/')
            ]);

            // Calculate basic statistics
            const totalUsers = usersResponse.length || 0;
            const totalCourses = coursesResponse.courses?.length || 0;

            setStats({
                totalUsers,
                totalCourses
            });
        } catch (error) {
            console.error('Failed to fetch admin statistics:', error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleUserManagement = () => {
        navigate('/admin/user-management');
    };

    const handleCourseManagement = () => {
        navigate('/admin/course-management');
    };

    const handleSystemAnalytics = () => {
        navigate('/admin/audit-logs');
    };

    return (
        <Container maxWidth="xl" sx={{mt: 4, pb: 10}}>
            <Box sx={{mb: 4}}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Administration Dashboard
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Welcome to the admin panel. Manage users, courses, and view system analytics from here.
                </Typography>
            </Box>

            {error && (
                <Box sx={{mb: 3}}>
                    <Typography variant="body2" color="error">
                        Error loading statistics: {error}
                    </Typography>
                </Box>
            )}

            {/* Management Action Cards */}
            <Grid container spacing={3} sx={{mb: 4, justifyContent: 'flex-start'}}>
                <Grid size="auto">
                    <Card sx={{height: 120, width: 320, minWidth: 320}}>
                        <CardActionArea onClick={handleUserManagement} sx={{height: '100%'}}>
                            <CardContent sx={{p: 2, height: '100%', display: 'flex', flexDirection: 'column'}}>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    mb: 2
                                }}>
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                                        <UsersIcon color="primary" sx={{fontSize: 40}}/>
                                        <Typography variant="h6" component="div">
                                            User Management
                                        </Typography>
                                    </Box>
                                    <ArrowIcon color="action"/>
                                </Box>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    mt: 'auto'
                                }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Total Users:
                                    </Typography>
                                    <Typography variant="h5" color="primary">
                                        {loading ? <CircularProgress size={20}/> : stats.totalUsers}
                                    </Typography>
                                </Box>
                            </CardContent>
                        </CardActionArea>
                    </Card>
                </Grid>

                <Grid size="auto">
                    <Card sx={{height: 120, width: 320, minWidth: 320}}>
                        <CardActionArea onClick={handleCourseManagement} sx={{height: '100%'}}>
                            <CardContent sx={{p: 2, height: '100%', display: 'flex', flexDirection: 'column'}}>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    mb: 2
                                }}>
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                                        <CourseIcon color="secondary" sx={{fontSize: 40}}/>
                                        <Typography variant="h6" component="div">
                                            Course Management
                                        </Typography>
                                    </Box>
                                    <ArrowIcon color="action"/>
                                </Box>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    mt: 'auto'
                                }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Total Courses:
                                    </Typography>
                                    <Typography variant="h5" color="secondary">
                                        {loading ? <CircularProgress size={20}/> : stats.totalCourses}
                                    </Typography>
                                </Box>
                            </CardContent>
                        </CardActionArea>
                    </Card>
                </Grid>

                <Grid size="auto">
                    <Card sx={{height: 120, width: 320, minWidth: 320}}>
                        <CardActionArea onClick={handleSystemAnalytics} sx={{height: '100%'}}>
                            <CardContent sx={{p: 2, height: '100%', display: 'flex', flexDirection: 'column'}}>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    mb: 2
                                }}>
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                                        <AnalyticsIcon color="success" sx={{fontSize: 40}}/>
                                        <Typography variant="h6" component="div">
                                            Audit Logs
                                        </Typography>
                                    </Box>
                                    <ArrowIcon color="action"/>
                                </Box>
                                <Box sx={{mt: 'auto'}}>
                                    <Typography variant="body2" color="text.secondary">
                                        Audit logs, performance metrics
                                    </Typography>
                                </Box>
                            </CardContent>
                        </CardActionArea>
                    </Card>
                </Grid>
            </Grid>

            {/* User Analytics Section */}
            <Box sx={{mt: 4}}>
                <UserAnalyticsChart accessToken={accessToken} />
            </Box>
        </Container>
    );
};

export default AdminDashboard;